import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Select from 'react-select';
import { useQuery, useMutation } from 'react-apollo';
import { UPSERT_SPECIALIZATION_MAPPING, GET_CATEGORIES } from '../queries';
import { displayError, displaySuccess } from '../utilities';
import LookupSelect from './LookupSelect';

const AddEditMappingModal = ({ isOpen, toggle, isEdit, mapping }) => {
  const { data: categoryData } = useQuery(GET_CATEGORIES);

  const categoryOptions = (
    (categoryData && categoryData.getCategories) ||
    []
  ).map(cat => ({
    label: cat.name,
    value: cat.id
  }));

  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (mapping) {
      setSelectedSpecialization(mapping.specialization);

      if (mapping.category) {
        setSelectedCategory({
          label: mapping.category.name,
          value: mapping.category.id
        });
      } else {
        setSelectedCategory(null);
      }
    } else {
      setSelectedSpecialization('');
      setSelectedCategory(null);
    }
  }, [mapping, isOpen]);

  const [upsertMapping, { loading }] = useMutation(
    UPSERT_SPECIALIZATION_MAPPING,
    {
      refetchQueries: ['GET_SPECIALIZATION_MAPPING'],

      onCompleted: data => {
        const response = data.upsertSpecializationMapping;
        if (response.status) {
          displaySuccess(
            isEdit
              ? 'Mapping updated successfully'
              : 'Mapping created successfully'
          );
          toggle();
        } else {
          displayError(response.message || 'Failed to save mapping');
        }
      },

      onError: err => {
        displayError(err);
      }
    }
  );

  const handleSave = async () => {
    if (!selectedSpecialization || !selectedCategory) return;

    await upsertMapping({
      variables: {
        input: [
          {
            id: isEdit ? mapping.id : undefined,
            specialization: selectedSpecialization,
            specialty: selectedCategory.label,
            categoryId: selectedCategory.value
          }
        ]
      }
    });
  };

  const isSaveDisabled = !selectedSpecialization || !selectedCategory;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {isEdit ? 'Edit Mapping' : 'Add Mapping'}
      </ModalHeader>

      <ModalBody>
        <div className="mapping-form">
          <div className="mapping-form-row">
            <label>AMS Specialty:</label>

            <div className="mapping-input">
              <LookupSelect
                lookup="spl"
                isMulti={false}
                value={selectedSpecialization}
                onChange={setSelectedSpecialization}
                className="mapping-select"
                placeholder="Select Specialty"
              />
            </div>
          </div>

          <div className="mapping-form-row">
            <label>Category:</label>

            <div className="mapping-input">
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={setSelectedCategory}
                className="mapping-select"
                placeholder="Select Category"
              />
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="mapping-modal-footer">
        <Button
          className="mapping-save-btn"
          onClick={handleSave}
          disabled={loading || isSaveDisabled}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>

        <Button className="mapping-cancel-btn" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditMappingModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  isEdit: PropTypes.bool,
  mapping: PropTypes.object
};

export default AddEditMappingModal;
